import { logger } from '@biproxi/logger';
import React from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@apollo/client';
import IChannelQuery from '@biproxi/models/interfaces/IChannelQuery';
import { IChannelGraphQL } from '@biproxi/models/interfaces/IChannel';
import Loader, { LoaderSizes } from '../elements/Loader';
import Input, { InputSizeEnum, InputTypesEnum } from '../elements/Input';
import Colors from '../styles/Colors';
import MessageTable from '../components/MessageTable';
import NoContent from '../components/NoContent';
import Icons from '../elements/Icons';
import LIST_CHANNELS from '../graphql/queries/channels.query';
import { useAppSelector } from '../redux/store';
import { ListingSelectors } from '../redux/listing.redux';
import useUpdateChannels from '../hooks/useUpdateChannels.hook';
import PaginationLimitsEnum from '../models/enums/PaginationLimitsEnum';

const Container = styled.div`
  margin-top: -24px;
`;

const LoaderContainer = styled.div`
  height: 30vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type ListingDetailsMessagesProps = {};

const ListingDetailsMessages: React.FC<ListingDetailsMessagesProps> = () => {
  /** State */
  const [search, setSearch] = React.useState('');

  /** Actions */
  const listingId = useAppSelector(ListingSelectors.selectedListingId);

  /** GraphQL */
  interface Data {
    channels: IChannelGraphQL[];
  }

  interface Vars {
    params: IChannelQuery;
  }

  const { data, loading, fetchMore } = useQuery<Data, Vars>(LIST_CHANNELS, {
    variables: {
      params: {
        listingId,
        search,
        pagination: {
          limit: PaginationLimitsEnum.Channels,
          cursor: '',
        },
      },
    },
    context: {
      debounceKey: 'QUERY_CHANNELS',
    },
    onError: (error) => {
      logger.error('ListingDetailsMessages error', error);
    },
  });

  const [channels, setChannels] = React.useState(data?.channels || []);

  /** Hooks */
  useUpdateChannels({ data, channels, setChannels });

  /** Render */
  return (
    <Container data-cy="messages-list-container">
      <Input
        margin="24px 0 16px"
        placeholder="Search Messages"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        clear={() => setSearch('')}
        inputType={InputTypesEnum.Search}
        inputSize={InputSizeEnum.Small}
        width="100%"
      />
      {loading ? (
        <LoaderContainer>
          <Loader size={LoaderSizes.Large} color={Colors.Brand700 || Colors.Blurple700} />
        </LoaderContainer>
      ) : (
        <>
          {channels?.length > 0 ? (
            <MessageTable
              fetchMore={fetchMore}
              channels={channels}
            />
          ) : (
            <NoContent
              height="176px"
              icon={search ? Icons.SearchRegular : Icons.CommentAltLinesLight}
              text={search
                ? 'No search results. Please try different search terms.'
                : 'No messages yet. Messages you have sent or received for this listing will appear here.'}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default ListingDetailsMessages;
